import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import {
  FilterMultiSelect,
  FilterSelect,
} from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import { DATASET_STATUSES, DATASET_TYPES } from './filterDatasets';

export const datasetListQueryParser = z.object({
  search: z.coerce.string().optional(),
  type: z.enum(DATASET_TYPES).optional().catch(undefined),
  status: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(DATASET_STATUSES).array())
    .optional()
    .catch(undefined),
});

export type DatasetListQuery = z.infer<typeof datasetListQueryParser>;

export const useDatasetListFilters = ({
  setFilters,
}: {
  setFilters: (state: DatasetListQuery) => void;
}) => {
  const intl = useIntl();

  return useMemo(() => {
    const getTypeLabel = (value: string) =>
      R.isIncludedIn(value, DATASET_TYPES)
        ? intl.formatMessage({
            id: `features.datasets.list.section.${value}.title`,
          })
        : value;

    const getStatusLabel = (value: string) =>
      R.isIncludedIn(value, DATASET_STATUSES)
        ? intl.formatMessage({
            id: `features.datasets.list.filters.status.${value}`,
          })
        : value;

    return [
      createSettings({
        filterKey: 'type',
        name: intl.formatMessage({
          id: 'features.datasets.list.filters.type',
        }),
        options: [...DATASET_TYPES],
        getOptionLabel: getTypeLabel,
        getChipLabel: getTypeLabel,
        valueGetter: (currentValue: DatasetListQuery) => currentValue.type,
        setFilter: value => setFilters({ type: value }),
        Component: FilterSelect,
      }),
      createSettings({
        filterKey: 'status',
        name: intl.formatMessage({
          id: 'features.datasets.list.filters.status',
        }),
        options: [...DATASET_STATUSES],
        getOptionLabel: getStatusLabel,
        getChipLabel: value => value.map(getStatusLabel).join(', '),
        valueGetter: (currentValue: DatasetListQuery) => currentValue.status,
        setFilter: value => setFilters({ status: value }),
        Component: FilterMultiSelect,
      }),
    ];
  }, [intl, setFilters]);
};
